import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError, createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { AuthService } from 'proto/gen/ts/shirt/v1/auth_pb';
import { CheckoutService } from 'proto/gen/ts/shirt/v1/checkout_pb';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../routes';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { formatPrice } from '../utils/format';
import { formatAddress } from '../utils/orderTypes';
import { transport } from '../utils/transport';
const checkoutClient = createClient(CheckoutService, transport);
const authClient = createClient(AuthService, transport);
function CreateAccountPrompt({ email, sessionId }) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(false);
    const login = useAuthStore((s)=>s.login);
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const resp = await authClient.createAccountFromOrder({
                email,
                password,
                stripeSessionId: sessionId
            });
            login(resp.token, resp.userId, resp.username, resp.role, resp.email);
            setCreated(true);
        } catch (err) {
            setError(err instanceof ConnectError ? err.message : 'Failed to create account');
        } finally{
            setLoading(false);
        }
    };
    if (created) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "bg-green-50 border border-green-200 p-4 text-center",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-green-700 font-semibold",
                    children: "Account created!"
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.accountOrders,
                    className: "text-sm underline mt-2 inline-block",
                    children: "View your orders"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("form", {
        onSubmit: handleSubmit,
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-stone-500",
                children: [
                    "Your email: ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: email
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-red-600",
                children: error
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        htmlFor: "account-password",
                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                        children: "Choose a password"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        id: "account-password",
                        type: "password",
                        value: password,
                        onChange: (e)=>setPassword(e.target.value),
                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none",
                        placeholder: "At least 6 characters",
                        required: true,
                        minLength: 6
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "submit",
                disabled: loading,
                className: "w-full py-3 bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-stone-700 disabled:opacity-60",
                children: loading ? 'Creating...' : 'Create Account'
            })
        ]
    });
}
export default function CheckoutConfirmationPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const clearCart = useCartStore((s)=>s.clearCart);
    const isAuthenticated = useAuthStore((s)=>s.isAuthenticated);
    useEffect(()=>{
        if (sessionId) clearCart();
    }, [
        sessionId,
        clearCart
    ]);
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'order',
            sessionId
        ],
        queryFn: ()=>checkoutClient.getOrderBySession({
                sessionId: sessionId ?? ''
            }),
        enabled: !!sessionId,
        retry: 3,
        retryDelay: 2000
    });
    const { data: orderDetail } = useQuery({
        queryKey: [
            'orderDetail',
            data?.id,
            data?.email
        ],
        queryFn: ()=>checkoutClient.lookupGuestOrder({
                orderId: data?.id ?? '',
                email: data?.email ?? ''
            }),
        enabled: !!data?.id && !!data?.email
    });
    let items = [];
    try {
        if (orderDetail?.itemsJson) {
            items = JSON.parse(orderDetail.itemsJson);
        }
    } catch  {
    // ignore
    }
    const shipping = orderDetail?.shippingAddress ? formatAddress(orderDetail.shippingAddress) : null;
    if (!sessionId) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-display text-4xl text-stone-900 uppercase mb-4",
                    children: "No Order Found"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-stone-500 mb-6",
                    children: "No session ID was provided."
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Continue Shopping"
                })
            ]
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center animate-pulse",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "h-8 bg-stone-100 w-1/3 mx-auto mb-4"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "h-4 bg-stone-50 w-1/2 mx-auto mb-2"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "h-4 bg-stone-50 w-1/4 mx-auto"
                })
            ]
        });
    }
    if (isError || !data) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-display text-4xl text-stone-900 uppercase mb-4",
                    children: "Order Confirmation"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-stone-500 mb-2",
                    children: "Your payment was received. We're processing your order."
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-stone-400 mb-8",
                    children: "A confirmation email will be sent shortly."
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Continue Shopping"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-center mb-10",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6",
                        children: /*#__PURE__*/ _jsxs("svg", {
                            className: "w-8 h-8 text-green-600",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor",
                            "aria-hidden": "true",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Success"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    strokeWidth: 2,
                                    d: "M5 13l4 4L19 7"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "font-display text-5xl text-stone-900 uppercase tracking-wide mb-2",
                        children: "Thank You!"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-stone-600",
                        children: "Your order has been placed successfully."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "max-w-md mx-auto space-y-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border-2 border-stone-100 bg-white p-6 space-y-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-500 text-xs uppercase tracking-wider",
                                        children: "Order ID"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-stone-900 text-sm font-mono",
                                        children: [
                                            data.id.slice(0, 8),
                                            "..."
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-500 text-xs uppercase tracking-wider",
                                        children: "Status"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-sm font-semibold capitalize text-stone-900",
                                        children: data.status
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-500 text-xs uppercase tracking-wider",
                                        children: "Email"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-900 text-sm",
                                        children: data.email
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "border-t-2 border-stone-100 pt-4 flex justify-between items-baseline",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-900 font-semibold uppercase tracking-wider text-xs",
                                        children: "Total"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-stone-900 font-display text-3xl uppercase",
                                        children: formatPrice(Number(data.totalCents))
                                    })
                                ]
                            })
                        ]
                    }),
                    !isAuthenticated && data.email ? /*#__PURE__*/ _jsxs("div", {
                        className: "bg-green-50 border-2 border-green-200 p-6 space-y-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-center space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-semibold text-green-800",
                                        children: "Create an account to track your order"
                                    }),
                                    /*#__PURE__*/ _jsxs("ul", {
                                        className: "text-xs text-green-700 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("li", {
                                                children: "Get shipping updates and tracking"
                                            }),
                                            /*#__PURE__*/ _jsx("li", {
                                                children: "View your full order history"
                                            }),
                                            /*#__PURE__*/ _jsx("li", {
                                                children: "Faster checkout next time"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(CreateAccountPrompt, {
                                email: data.email,
                                sessionId: sessionId
                            })
                        ]
                    }) : isAuthenticated ? /*#__PURE__*/ _jsx("div", {
                        className: "text-center",
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: ROUTES.accountOrders,
                            className: "text-sm underline text-stone-700 hover:text-stone-900",
                            children: "View your orders"
                        })
                    }) : null,
                    items.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "border-2 border-stone-100 bg-white p-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3",
                                children: "Items"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "space-y-4",
                                children: items.map((item)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex gap-4 border-b border-stone-100 pb-4 last:border-0 last:pb-0",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "w-16 h-16 flex-shrink-0 bg-stone-100 overflow-hidden",
                                                children: item.image_url && /*#__PURE__*/ _jsx("img", {
                                                    src: item.image_url,
                                                    alt: item.name,
                                                    className: "w-full h-full object-cover"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex-1 min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm font-semibold text-stone-900",
                                                        children: item.name
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center justify-between mt-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "text-xs text-stone-500",
                                                                children: [
                                                                    "Qty: ",
                                                                    item.quantity
                                                                ]
                                                            }),
                                                            item.price_cents > 0 && /*#__PURE__*/ _jsx("span", {
                                                                className: "text-sm text-stone-900",
                                                                children: formatPrice(item.price_cents * item.quantity)
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }, item.product_id))
                            })
                        ]
                    }),
                    shipping && /*#__PURE__*/ _jsxs("div", {
                        className: "border-2 border-stone-100 bg-white p-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3",
                                children: "Shipping To"
                            }),
                            shipping.name && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-semibold text-stone-900",
                                children: shipping.name
                            }),
                            shipping.lines.map((line)=>/*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-stone-600",
                                    children: line
                                }, line))
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-stone-400 text-center",
                        children: [
                            "Save your order ID ",
                            /*#__PURE__*/ _jsxs("strong", {
                                className: "font-mono",
                                children: [
                                    data.id.slice(0, 8),
                                    "..."
                                ]
                            }),
                            " to look it up later."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-center mt-10",
                children: /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Continue Shopping"
                })
            })
        ]
    });
}
